/**
 * TextInput Component
 */

import React from 'react';
import type { ValidationMessage } from '../../types/validation';

export interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  errors?: ValidationMessage[];
  warnings?: ValidationMessage[];
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  onChange,
  errors = [],
  warnings = [],
  disabled = false,
  required = false,
  placeholder,
  className = '',
}) => {
  const hasError = errors.length > 0;
  const hasWarning = warnings.length > 0;

  return (
    <div className={`mb-3 ${className}`}>
      <label className="block text-xs font-semibold text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={`
          w-full px-3 py-2 text-sm border rounded
          ${hasError ? 'border-red-500 focus:ring-red-500' : ''}
          ${hasWarning && !hasError ? 'border-yellow-500 focus:ring-yellow-500' : ''}
          ${!hasError && !hasWarning ? 'border-gray-300 focus:ring-indigo-500' : ''}
          focus:outline-none focus:ring-2
          disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
        `}
      />

      {errors.map((error, idx) => (
        <div key={idx} className="text-xs text-red-600 mt-1">
          {error.message}
        </div>
      ))}

      {warnings.map((warning, idx) => (
        <div key={idx} className="text-xs text-yellow-600 mt-1">
          {warning.message}
        </div>
      ))}
    </div>
  );
};
